import { createFileRoute } from "@tanstack/react-router";
import { SeoPage } from "@/components/SeoPage";
import { faqJsonLd, articleJsonLd } from "@/lib/seo-jsonld";
import { breadcrumbJsonLd } from "@/lib/seo-jsonld";

const faqs = [
  { q: "Vad är en PBI-krets?", a: "PBI står för Personer med Betydande Inflytande. Det är personer som genom ägande, styrelseposter eller annan ställning har faktiskt inflytande över verksamheten med serveringstillstånd." },
  { q: "Vilka räknas in i PBI-kretsen?", a: "Ägare och delägare, VD och styrelseledamöter, finansiärer med kontroll samt anhöriga eller bulvaner som agerar för någons räkning. Kommunen prövar lämpligheten för alla i kretsen." },
  { q: "Måste alla i PBI-kretsen göra kunskapsprovet?", a: "Minst hälften av PBI-kretsen ska ha godkänt kunskapsprov i alkohollagen, och det ska alltid finnas minst en person med godkänt prov tillgänglig under serveringen." },
];

export const Route = createFileRoute("/pbi-krets")({
  component: () => (
    <SeoPage
      breadcrumbs={[
        { label: "Hem", to: "/" },
        { label: "Guider", to: "/serveringstillstand-och-kunskapsprov" },
        { label: "PBI-krets" },
      ]}
      title="PBI-krets – personer med betydande inflytande"
      intro="PBI-kretsen är avgörande för att få serveringstillstånd. Här förklarar vi vilka som räknas in, vad kommunen granskar och vilka kunskapskrav som ställs."
      sections={[
        { h2: "Vad betyder PBI?", body: "PBI står för Personer med Betydande Inflytande. Begreppet används av kommunen för att identifiera alla som har faktiskt inflytande över en serveringsverksamhet – inte bara den formella tillståndshavaren." },
        { h2: "Vilka ingår i kretsen?", body: "Typiskt: ägare och delägare över en viss andel, styrelseledamöter, VD, firmatecknare, finansiärer som kan utöva kontroll, och i vissa fall anhöriga eller bulvaner. Kommunen gör en bred lämplighetsprövning av samtliga." },
        { h2: "Lämplighetsprövning", body: "Kommunen granskar personlig och ekonomisk lämplighet: brottsbelastning, skatteskulder, ekonomisk skötsamhet och tidigare misskötta tillstånd. Anmärkningar hos någon i kretsen kan leda till att tillståndet nekas eller återkallas." },
        { h2: "Kunskapskravet", body: "Minst hälften av PBI-kretsen ska ha avlagt godkänt kunskapsprov i alkohollagen. Under servering ska alltid minst en person med godkänt prov vara närvarande. Detta är en återkommande fråga i provet." },
        { h2: "Förändringar i PBI-kretsen", body: "Större förändringar i ägande eller styrelse ska anmälas till kommunen utan dröjsmål. En ny PBI-person måste lämplighetsprövas innan personen får tillträda." },
      ]}
      faqs={faqs}
      related={{
        title: "Relaterade sidor",
        links: [
          { to: "/serveringstillstand-och-kunskapsprov", label: "Hub – alla guider" },
          { to: "/alkohollagen-kapitel-8", label: "Alkohollagen kapitel 8" },
          { to: "/serveringstillstand", label: "Serveringstillstånd" },
          { to: "/kunskapsprov-alkohollagen", label: "Kunskapsprov i alkohollagen" },
          { to: "/foretag", label: "Företagslösning" },
        ],
      }}
    />
  ),
  head: () => ({
    meta: [
      { title: "PBI-krets – personer med betydande inflytande | Alkoholprovet" },
      { name: "description", content: "PBI-krets förklarad: vilka räknas in, hur lämplighetsprövningen går till och kunskapskraven för serveringstillstånd." },
      { property: "og:title", content: "PBI-krets – personer med betydande inflytande" },
      { property: "og:description", content: "Vilka räknas in i PBI-kretsen, lämplighetsprövning och kunskapskrav." },
      { property: "og:url", content: "https://alkoholprovet.se/pbi-krets" },
    ],
    links: [{ rel: "canonical", href: "https://alkoholprovet.se/pbi-krets" }],
    scripts: [
      faqJsonLd(faqs),
      breadcrumbJsonLd([
        { label: "Hem", url: "https://alkoholprovet.se/" },
        { label: "Guider", url: "https://alkoholprovet.se/serveringstillstand-och-kunskapsprov" },
        { label: "PBI-krets", url: "https://alkoholprovet.se/pbi-krets" },
      ]),
      articleJsonLd({
        url: "https://alkoholprovet.se/pbi-krets",
        headline: "PBI-krets – personer med betydande inflytande | Alkoholprovet",
        description: "PBI-krets förklarad: vilka räknas in, hur lämplighetsprövningen går till och kunskapskraven för serveringstillstånd.",
        datePublished: "2026-01-15",
        dateModified: "2026-06-10",
      }),
    ],
  }),
});
